import type { FunctionComponent } from 'react';

import dayjs from 'dayjs';
import { Button, Stack } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useTranslation } from 'i18n';

import CardEmpty from 'components/CardEmpty/CardEmpty';
import { formatHoursArray } from 'helpers/format';
import { isStoreClosedHelper } from 'helpers/menuManagementHours';
import DockedModal from 'primitives/DockedModal/DockedModal';
import { breakpoints } from 'styles/theme.variables';
import type { MenuDayHours, MenuHour } from 'types/menuHours';

import { HolidayHoursCard } from './HolidayHoursCard';
import { HoursCard } from './HoursCard';
import type { HoursModalMenu, HoursModalProps } from './types';

const NS = 'hoursModal';

function hasScheduledHours(days: MenuDayHours[]): boolean {
  return days.some(day => day.openTimes.some(time => !isStoreClosedHelper(time)));
}

function getDeliveryHours(menu: HoursModalMenu): MenuHour[] {
  return menu.deliveryMatchesPickup === false ? (menu.deliveryHours ?? []) : [];
}

export const HoursModal: FunctionComponent<HoursModalProps> = ({ menu, opened, onClose }) => {
  const { t } = useTranslation(NS);
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.maxMobile})`);
  const modalPadding = isMobile ? 'md' : 'xl';
  const today = dayjs().format('dddd').toLowerCase();

  const pickupHours: MenuDayHours[] = formatHoursArray(menu.itemHours);
  const deliveryHours: MenuDayHours[] = formatHoursArray(getDeliveryHours(menu));

  return (
    <DockedModal
      opened={opened}
      onClose={onClose}
      title={t('HoursModal.title', { menuName: menu.name })}
      testId="hoursModal"
      size={isMobile ? 'xl' : 'md'}
      padding={modalPadding}
      dockButtons={<Button onClick={onClose}>{t('HoursModal.done')}</Button>}
      dockProps={{ noShadow: true, px: modalPadding }}
    >
      <Stack>
        {!!menu.specialHours?.length && <HolidayHoursCard specialHours={menu.specialHours} />}

        {hasScheduledHours(pickupHours) ? (
          <>
            <HoursCard
              label={t('HoursModal.pickupHours')}
              hours={pickupHours}
              today={today}
              testId="pickupHoursCard"
            />
            {!!deliveryHours.length && (
              <HoursCard
                label={t('HoursModal.deliveryHours')}
                hours={deliveryHours}
                today={today}
                testId="deliveryHoursCard"
              />
            )}
          </>
        ) : (
          <CardEmpty message={t('HoursModal.noScheduledHours')} />
        )}
      </Stack>
    </DockedModal>
  );
};
